import { X, User, Calendar, Tag, FileText, Bot, MessageSquare, CheckCircle2, Clock } from 'lucide-react';
import { Modal } from 'react-bootstrap';

interface LogDetailsModalProps {
  data: any;
  onClose: () => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const isoString = date.toISOString();
  const year = isoString.substring(0, 4);
  const month = isoString.substring(5, 7);
  const day = isoString.substring(8, 10);
  const hour = isoString.substring(11, 13);
  const minute = isoString.substring(14, 16);
  const second = isoString.substring(17, 19);
  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
};

const FIELD_LABELS: Record<string, string> = {
  tipoSolicitacao: 'Tipo de solicitação',
  idIFC: 'ID Infracommerce',
  tipoMecanica: 'Tipo de mecânica',
  tipoLista: 'Tipo de lista',
  idListaOriginal: 'ID lista original',
  recompraDisponivel: 'Recompra disponível',
  nomePromo: 'Nome interno da promoção',
  nomeExibicao: 'Nome de exibição',
  descricao: 'Descrição da promoção',
  descricaoInterna: 'Descrição interna',
  industria: 'Indústria',
  multiPatrocinador: 'Multi patrocinador',
  patrocinadores: 'Patrocinadores',
  cumulativa: 'Cumulativa',
  inicioVigencia: 'Início da vigência',
  fimVigencia: 'Fim da vigência',
  budgetAtrelado: 'Budget atrelado',
  budgetValor: 'Valor do budget',
  sellersIncluidos: 'Sellers incluídos',
  sellersExcecao: 'Sellers exceção',
  cnpjsIncluidos: 'CNPJs incluídos',
  cnpjsExcecao: 'CNPJs exceção',
  canais: 'Canais',
  tipoPedido: 'Tipo de pedido',
  tipoDesconto: 'Tipo de desconto',
  valorDesconto: 'Valor do desconto',
  criterioDesconto: 'Critério de desconto',
  qtdMinima: 'Quantidade mínima',
  qtdMaxima: 'Quantidade máxima',
  valorMinimo: 'Valor mínimo',
  valorMaximo: 'Valor máximo',
  numeroCard: 'Número do card',
  qtdCardsFilhos: 'Quantidade de cards filhos',
  tipoItemIncluido: 'Tipo de item incluído',
  itensIncluidos: 'Itens incluídos',
  tipoItemExcluido: 'Tipo de item excluído',
  itensExcluidos: 'Itens excluídos',
  eansObrigatorios: 'EANs obrigatórios',
  skusObrigatorios: 'SKUs obrigatórios',
  skuCombo: 'SKU é combo',
  tipoFamilia: 'Tipo de família',
  nomeFamilia: 'Nome da família',
  industriaFamilia: 'Indústria da família',
  obsFamilia: 'Observação da família',
  skusFamilia: 'SKUs da família',
  qtdEANsDistintos: 'Quantidade de EANs distintos',
  qtdMinimaAtivacao: 'Quantidade mínima para ativação',
  qtdMaximaAtivacao: 'Quantidade máxima para ativação',
  qtdFamiliasAtivacao: 'Quantidade de famílias para ativação',
  familiaObrigatoria: 'Família obrigatória',
  posicaoFamilia: 'Posição da família',
  descontoFamilia: 'Desconto da família',
  posicaoLista: 'Posição na lista',
  secaoLista: 'Seção da lista',
  cupom: 'Cupom',
  codigoCupom: 'Código do cupom',
  qtdCupons: 'Quantidade de cupons',
  usosPorCliente: 'Usos por cliente',
};

const parseLogJson = (value: any) => {
  if (!value) return null;
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

const isBriefingAgentLog = (data: any, json: any) => {
  return data?.type === 'briefing-promocoes-agent-chat' || json?.source === 'briefing-promocoes-agent';
};

const humanizeFieldKey = (key: string): string => {
  const childMatch = key.match(/(.+)__child(\d+)$/);
  const baseKey = childMatch ? childMatch[1] : key;
  const suffix = childMatch ? ` (Filho ${childMatch[2]})` : '';
  const label = FIELD_LABELS[baseKey] || baseKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase());

  return `${label}${suffix}`;
};

const formatFieldValue = (value: any): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);

  const text = String(value ?? '');
  return text.length > 260 ? `${text.substring(0, 260)}...` : text;
};

const normalizeMessages = (json: any): any[] => {
  if (Array.isArray(json?.conversation?.messages)) return json.conversation.messages;
  if (Array.isArray(json?.conversation?.exchange)) return json.conversation.exchange;
  if (Array.isArray(json?.conversation?.history)) return json.conversation.history;
  return [];
};

const normalizeFilledValues = (json: any): Record<string, any> => {
  return json?.form?.filledValues ||
    json?.form?.suggestedValues ||
    json?.form?.latestSuggestedValues ||
    {};
};

const LogDetailsModal: React.FC<LogDetailsModalProps> = ({ data, onClose }) => {
  if (!data) return null;
  const json = data.json || parseLogJson(data.data);
  const isAgentLog = isBriefingAgentLog(data, json);

  const renderJsonData = (jsonData: any) => {
    if (!jsonData) return null;

    return (
      <pre className="bg-light p-4 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
        <code>{JSON.stringify(jsonData, null, 2)}</code>
      </pre>
    );
  };

  const renderAgentLog = () => {
    const messages = normalizeMessages(json);
    const filledValues = normalizeFilledValues(json);
    const filledEntries = Object.entries(filledValues);
    const interactions = Array.isArray(json?.form?.interactions) ? json.form.interactions : [];
    const status = json?.status === 'error'
      ? 'Erro'
      : json?.status === 'missing-template'
        ? 'Sem template'
        : 'Concluído';
    const statusColor = json?.status === 'error' ? 'danger' : json?.status === 'missing-template' ? 'warning' : 'success';

    return (
      <>
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">Conversa do Agente de Briefing</h2>
                <div className="text-gray-600 fs-7">
                  {json?.templateName || 'Template não informado'} · {data.user}
                </div>
              </div>
              <button
                className="btn btn-sm btn-icon btn-active-color-primary"
                onClick={onClose}
              >
                <X size={24} />
              </button>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-4">
          <div className="row g-4 mb-5">
            <div className="col-md-3">
              <div className="border border-gray-300 rounded p-4 h-100">
                <div className="d-flex align-items-center gap-3">
                  <span className="btn btn-icon btn-light-primary">
                    <Bot size={18} />
                  </span>
                  <div>
                    <div className="text-gray-600 fs-8 fw-semibold">Template</div>
                    <div className="text-gray-900 fw-bold fs-7">{json?.templateName || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border border-gray-300 rounded p-4 h-100">
                <div className="d-flex align-items-center gap-3">
                  <span className="btn btn-icon btn-light-info">
                    <MessageSquare size={18} />
                  </span>
                  <div>
                    <div className="text-gray-600 fs-8 fw-semibold">Mensagens</div>
                    <div className="text-gray-900 fw-bold fs-7">{messages.length}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border border-gray-300 rounded p-4 h-100">
                <div className="d-flex align-items-center gap-3">
                  <span className="btn btn-icon btn-light-success">
                    <CheckCircle2 size={18} />
                  </span>
                  <div>
                    <div className="text-gray-600 fs-8 fw-semibold">Campos preenchidos</div>
                    <div className="text-gray-900 fw-bold fs-7">{filledEntries.length}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border border-gray-300 rounded p-4 h-100">
                <div className="d-flex align-items-center gap-3">
                  <span className={`btn btn-icon btn-light-${statusColor}`}>
                    <Clock size={18} />
                  </span>
                  <div>
                    <div className="text-gray-600 fs-8 fw-semibold">Status</div>
                    <span className={`badge badge-light-${statusColor}`}>{status}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-lg-7">
              <div className="border border-gray-300 rounded p-4 h-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">Conversa</h5>
                  <span className="text-gray-500 fs-8">{formatDate(data.created_at)}</span>
                </div>
                <div className="d-flex flex-column gap-3" style={{ maxHeight: 560, overflowY: 'auto' }}>
                  {messages.length === 0 ? (
                    <div className="text-gray-500">Nenhuma mensagem registrada.</div>
                  ) : (
                    messages.map((message, index) => {
                      const isUser = message.role === 'user';
                      return (
                        <div
                          key={`${message.role}-${index}`}
                          className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          <div
                            className={`p-3 rounded ${isUser ? 'bg-primary text-white' : 'bg-light-primary text-gray-900 border border-primary border-opacity-25'}`}
                            style={{ maxWidth: '82%', whiteSpace: 'pre-wrap' }}
                          >
                            <div className={`fs-8 fw-semibold mb-1 ${isUser ? 'text-white-50' : 'text-primary'}`}>
                              {isUser ? 'Usuário' : 'Agente'}
                            </div>
                            <div className="fs-7">{message.content || '-'}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="border border-gray-300 rounded p-4 mb-5">
                <h5 className="fw-bold mb-4">Campos preenchidos pelo agente</h5>
                {filledEntries.length === 0 ? (
                  <div className="text-gray-500">Nenhum campo preenchido automaticamente.</div>
                ) : (
                  <div className="d-flex flex-column gap-3" style={{ maxHeight: 330, overflowY: 'auto' }}>
                    {filledEntries.map(([key, value]) => (
                      <div key={key} className="border-bottom border-gray-200 pb-3">
                        <div className="text-gray-700 fs-8 fw-bold mb-1">{humanizeFieldKey(key)}</div>
                        <div className="text-gray-900 fs-7">{formatFieldValue(value) || '-'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-gray-300 rounded p-4">
                <h5 className="fw-bold mb-4">Interações</h5>
                {interactions.length === 0 ? (
                  <div className="text-gray-500">Sem detalhes de interações.</div>
                ) : (
                  <div className="d-flex flex-column gap-3" style={{ maxHeight: 230, overflowY: 'auto' }}>
                    {interactions.map((interaction: any, index: number) => (
                      <div key={`interaction-${index}`} className="bg-light rounded p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold text-gray-800 fs-7">Troca {index + 1}</span>
                          <span className={`badge badge-light-${interaction.status === 'error' ? 'danger' : 'primary'}`}>
                            {interaction.suggestedFieldKeys?.length || 0} campo(s)
                          </span>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {(interaction.suggestedFieldKeys || []).slice(0, 8).map((field: string) => (
                            <span key={field} className="badge badge-light">
                              {humanizeFieldKey(field)}
                            </span>
                          ))}
                          {(interaction.suggestedFieldKeys || []).length > 8 && (
                            <span className="badge badge-light-primary">
                              +{interaction.suggestedFieldKeys.length - 8}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0">
          <button className="btn btn-light" onClick={onClose}>
            Fechar
          </button>
        </Modal.Footer>
      </>
    );
  };

  if (isAgentLog) {
    return renderAgentLog();
  }

  return (
    <>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="fw-bold mb-0">Detalhes do Log</h2>
            <button
              className="btn btn-sm btn-icon btn-active-color-primary"
              onClick={onClose}
            >
              <X size={24} />
            </button>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-4">
        <div className="d-flex flex-column gap-4">
          <div className="card bg-light-primary border-0">
            <div className="card-body p-4">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center bg-primary"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    minWidth: '40px',
                  }}
                >
                  <User size={20} className="text-white" />
                </div>
                <div className="flex-grow-1">
                  <div className="text-gray-600 fs-7 fw-semibold mb-1">Usuário</div>
                  <div className="text-gray-900 fs-5 fw-bold">{data.user}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="card border border-gray-300">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center bg-light-info"
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        minWidth: '36px',
                      }}
                    >
                      <Tag size={18} className="text-info" />
                    </div>
                    <div>
                      <div className="text-gray-600 fs-7 fw-semibold mb-1">Tipo</div>
                      <div className="text-gray-900 fs-6 fw-semibold">{data.type}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card border border-gray-300">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center bg-light-success"
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        minWidth: '36px',
                      }}
                    >
                      <Calendar size={18} className="text-success" />
                    </div>
                    <div>
                      <div className="text-gray-600 fs-7 fw-semibold mb-1">Data/Hora</div>
                      <div className="text-gray-900 fs-6 fw-semibold">
                        {formatDate(data.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border border-gray-300">
            <div className="card-body p-4">
              <div className="d-flex align-items-start gap-3 mb-3">
                <div
                  className="d-flex align-items-center justify-content-center bg-light-warning"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    minWidth: '36px',
                  }}
                >
                  <FileText size={18} className="text-warning" />
                </div>
                <div>
                  <div className="text-gray-600 fs-7 fw-semibold mb-1">Descrição</div>
                  <div className="text-gray-900 fs-6">{data.name}</div>
                </div>
              </div>
            </div>
          </div>

          {data.json && Object.keys(data.json).length > 0 && (
            <div className="card border border-gray-300">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-4">Dados do Log</h5>
                {renderJsonData(data.json)}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <button className="btn btn-light" onClick={onClose}>
          Fechar
        </button>
      </Modal.Footer>
    </>
  );
};

export default LogDetailsModal;
